import { useEffect, useMemo, useRef } from 'react';
import { GeoJSON, useMap, useMapEvents } from 'react-leaflet';
import type { FeatureCollection, Feature, Geometry } from 'geojson';
import L from 'leaflet';
import { useState } from 'react';

interface RegionOverlayProps {
  geoData: FeatureCollection;
  highlightedRegions?: string[];
  onRegionClick?: (regionName: string) => void;
}

// OKLCH strings kept in sync with src/index.css tokens.
// Avoid hex drift — if you change --signal-* tokens, update here too.
const REGION_BASE_FILL = 'oklch(0.95 0.008 155)';
const REGION_HIGHLIGHT_FILL = 'oklch(0.68 0.18 155)';
const REGION_MUTED_FILL = 'oklch(0.88 0.006 155)';
const REGION_HOVER_FILL = 'oklch(0.66 0.22 35)';
const REGION_STROKE = 'oklch(0.52 0.012 155)';
const REGION_HIGHLIGHT_STROKE = 'oklch(0.22 0.015 155)';

function getRegionStyle(
  feature: Feature<Geometry> | undefined,
  highlightedRegions: string[] | undefined,
  zoom: number
): L.PathOptions {
  const name = feature?.properties?.name as string | undefined;
  const hasHighlights = highlightedRegions && highlightedRegions.length > 0;

  // Zoom-based opacity: <=9 full, 10-12 fade, 13+ invisible
  let opacityMultiplier = 1;
  if (zoom >= 13) {
    opacityMultiplier = 0;
  } else if (zoom >= 10) {
    opacityMultiplier = 1 - (zoom - 9) / 4;
  }

  let fillColor = REGION_BASE_FILL;
  let fillOpacity = 0.35 * opacityMultiplier;
  let strokeColor = REGION_STROKE;
  let strokeWeight = 1;
  const strokeOpacity = 0.5 * opacityMultiplier;

  if (hasHighlights) {
    if (name && highlightedRegions!.includes(name)) {
      fillColor = REGION_HIGHLIGHT_FILL;
      fillOpacity = 0.32 * opacityMultiplier;
      strokeColor = REGION_HIGHLIGHT_STROKE;
      strokeWeight = 1.5;
    } else {
      fillColor = REGION_MUTED_FILL;
      fillOpacity = 0.2 * opacityMultiplier;
    }
  }

  return {
    fillColor,
    fillOpacity,
    color: strokeColor,
    weight: strokeWeight,
    opacity: strokeOpacity,
  };
}

export function RegionOverlay({
  geoData,
  highlightedRegions,
  onRegionClick,
}: RegionOverlayProps) {
  const map = useMap();
  const [zoom, setZoom] = useState(map.getZoom());
  const geoJsonRef = useRef<L.GeoJSON | null>(null);

  useMapEvents({
    zoomend: () => {
      setZoom(map.getZoom());
    },
  });

  // Update styles when zoom or highlights change
  useEffect(() => {
    if (geoJsonRef.current) {
      geoJsonRef.current.eachLayer((layer) => {
        const geoLayer = layer as L.GeoJSON;
        const feature = (geoLayer as unknown as { feature: Feature<Geometry> }).feature;
        const style = getRegionStyle(feature, highlightedRegions, zoom);
        (layer as L.Path).setStyle(style);
      });
    }
  }, [zoom, highlightedRegions]);

  const onEachFeature = useMemo(() => {
    return (feature: Feature<Geometry>, layer: L.Layer) => {
      const name = feature.properties?.name as string | undefined;

      if (name) {
        layer.bindTooltip(name, {
          className: 'region-tooltip',
          sticky: true,
          direction: 'top',
          offset: [0, -10],
        });
      }

      layer.on({
        click: (e) => {
          L.DomEvent.stopPropagation(e);
          if (name && onRegionClick) {
            onRegionClick(name);
          }
        },
        mouseover: (e) => {
          const target = e.target as L.Path;
          const currentZoom = map.getZoom();
          if (currentZoom < 13) {
            target.setStyle({
              fillColor: REGION_HOVER_FILL,
              fillOpacity: 0.38,
            });
          }
        },
        mouseout: (e) => {
          const target = e.target as L.Path;
          const f = (target as unknown as { feature: Feature<Geometry> }).feature;
          const currentZoom = map.getZoom();
          const style = getRegionStyle(f, highlightedRegions, currentZoom);
          target.setStyle(style);
        },
      });
    };
  }, [onRegionClick, highlightedRegions, map]);

  const style = useMemo(() => {
    return (feature?: Feature<Geometry>) => getRegionStyle(feature, highlightedRegions, zoom);
  }, [highlightedRegions, zoom]);

  // Use key to force re-render when onEachFeature changes (highlight changes)
  const key = useMemo(
    () => `regions-${(highlightedRegions ?? []).join(',')}`,
    [highlightedRegions]
  );

  return (
    <GeoJSON
      key={key}
      ref={(ref) => { geoJsonRef.current = ref; }}
      data={geoData}
      style={style}
      onEachFeature={onEachFeature}
    />
  );
}
